import { useState } from "react";
import { Mail, Phone, MessageCircle, Send, Reply, MailCheck, MailX, MailQuestion, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { updateContact, resendNotification, replyToContact, deleteContact, formatApiError } from "../lib/adminApi";

export const STATUS = [
    { value: "nuovo", label: "Nuova", cls: "bg-[#E30613] text-white" },
    { value: "letto", label: "Letta", cls: "bg-slate-200 text-[#0B1F3A]" },
    { value: "da_richiamare", label: "Da richiamare", cls: "bg-amber-400 text-[#0B1F3A]" },
    { value: "gestito", label: "Gestita", cls: "bg-emerald-500 text-white" },
    { value: "cliente", label: "Cliente / contratto", cls: "bg-[#0B1F3A] text-white" },
];

export const statusOf = (c) => STATUS.find((s) => s.value === (c.status || "nuovo")) || STATUS[0];

// wa.me vuole solo cifre con prefisso internazionale: numeri italiani senza prefisso → +39.
export const whatsappHref = (telefono) => {
    const raw = (telefono || "").trim();
    let digits = raw.replace(/\D/g, "");
    if (!digits) return null;
    if (raw.startsWith("00")) digits = digits.slice(2);
    else if (!raw.startsWith("+")) digits = `39${digits}`;
    return `https://wa.me/${digits}`;
};

const pill = "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-colors";

export const ContactActions = ({ c }) => (
    <div className="flex flex-wrap gap-2" data-testid={`admin-contact-actions-${c.id}`}>
        {c.telefono && (
            <a href={`tel:${c.telefono.replace(/[^\d+]/g, "")}`} data-testid={`admin-contact-call-${c.id}`} className={`${pill} bg-[#0B1F3A] text-white hover:bg-[#132b4f]`}>
                <Phone className="h-3.5 w-3.5" /> Chiama {c.telefono}
            </a>
        )}
        {c.telefono && (
            <a href={whatsappHref(c.telefono)} target="_blank" rel="noopener noreferrer" data-testid={`admin-contact-whatsapp-${c.id}`} className={`${pill} bg-[#25D366] text-white hover:bg-[#1ebe5b]`}>
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
        )}
        <a href={`mailto:${c.email}`} data-testid={`admin-contact-mailto-${c.id}`} className={`${pill} border border-slate-200 bg-white text-[#0B1F3A] hover:bg-slate-50`}>
            <Mail className="h-3.5 w-3.5" /> {c.email}
        </a>
    </div>
);

export const NotifyStatus = ({ c, onChanged }) => {
    const [busy, setBusy] = useState(false);
    const n = c.notify;
    const resend = async () => {
        setBusy(true);
        try {
            const r = await resendNotification(c.id);
            toast.success(`Notifica reinviata a ${r.to}`);
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    const base = "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider";
    return (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`admin-contact-notify-${c.id}`}>
            {!n && <span className={`${base} bg-slate-100 text-[#4F5D70]`}><MailQuestion className="h-3 w-3" /> Notifica email: esito non registrato</span>}
            {n?.status === "sent" && <span className={`${base} bg-emerald-50 text-emerald-700`}><MailCheck className="h-3 w-3" /> Notifica inviata a {(n.to || []).join(", ")}</span>}
            {n?.status === "failed" && <span className={`${base} bg-red-50 text-[#E30613]`} title={n.error}><MailX className="h-3 w-3" /> Notifica email NON inviata</span>}
            {n?.status !== "sent" && (
                <button type="button" onClick={resend} disabled={busy} data-testid={`admin-contact-resend-${c.id}`} className="inline-flex items-center gap-1 rounded-full border border-[#0B1F3A] px-3 py-1 text-[11px] font-bold text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white disabled:opacity-50">
                    <Send className="h-3 w-3" /> {busy ? "Invio…" : "Reinvia notifica"}
                </button>
            )}
            {n?.status === "failed" && n.error && <span className="text-[11px] text-[#94A3B8]">Motivo: {n.error}</span>}
        </div>
    );
};

export const ReplyBox = ({ c, onChanged }) => {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const send = async () => {
        setBusy(true);
        try {
            const r = await replyToContact(c.id, text.trim());
            toast.success(`Risposta inviata a ${r.to}${r.copy_status === "sent" ? ` (copia a ${r.copy_to.join(", ")})` : " — copia interna NON inviata"}`);
            setText("");
            setOpen(false);
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="mt-3" data-testid={`admin-contact-reply-${c.id}`}>
            {(c.replies || []).length > 0 && (
                <ul className="mb-2 space-y-1.5" data-testid={`admin-contact-replies-${c.id}`}>
                    {c.replies.map((r) => (
                        <li key={r.sent_at} className="rounded-lg border-l-2 border-emerald-500 bg-emerald-50/60 px-3 py-2 text-xs text-[#334155]">
                            <span className="font-bold text-emerald-700">Risposta inviata</span> il {new Date(r.sent_at).toLocaleString("it-IT")} · {r.by}
                            <p className="mt-1 whitespace-pre-line">{r.message}</p>
                        </li>
                    ))}
                </ul>
            )}
            {!open ? (
                <button type="button" onClick={() => setOpen(true)} data-testid={`admin-contact-reply-open-${c.id}`} className={`${pill} bg-[#E30613] text-white hover:bg-[#C20410]`}>
                    <Reply className="h-3.5 w-3.5" /> Rispondi via email
                </button>
            ) : (
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-xs text-[#4F5D70]">
                        A: <strong>{c.email}</strong> · Reply-To: info@ftmgroup.it · copia a info@ftmgroup.it e maurizio.ftm@gmail.com. L'email inizia con "Gentile {c.nome}," e termina con la firma FTM Group S.r.l. Inviando, la richiesta passa a "Letta" se era "Nuova".
                    </p>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={4000} placeholder="Scrivi qui la risposta al cliente…" data-testid={`admin-contact-reply-text-${c.id}`} className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#0B1F3A] focus:border-[#0B1F3A] focus:outline-none" />
                    <div className="mt-2 flex gap-2">
                        <button type="button" onClick={send} disabled={busy || text.trim().length < 2} data-testid={`admin-contact-reply-send-${c.id}`} className={`${pill} bg-[#E30613] text-white hover:bg-[#C20410] disabled:opacity-50`}>
                            <Send className="h-3.5 w-3.5" /> {busy ? "Invio…" : "Invia risposta"}
                        </button>
                        <button type="button" onClick={() => setOpen(false)} className={`${pill} border border-slate-200 text-[#0B1F3A] hover:bg-slate-50`}>Annulla</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export const DeleteButton = ({ c, onDeleted }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const remove = async () => {
        setBusy(true);
        try {
            await deleteContact(c.id);
            toast.success("Richiesta eliminata");
            setOpen(false);
            onDeleted();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <>
            <button type="button" onClick={() => setOpen(true)} data-testid={`admin-contact-delete-${c.id}`} className={`${pill} border border-red-200 bg-white text-[#E30613] hover:bg-red-50`}>
                <Trash2 className="h-3.5 w-3.5" /> Elimina
            </button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent data-testid={`admin-contact-delete-dialog-${c.id}`}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare definitivamente questa richiesta?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {c.nome}{c.azienda ? ` · ${c.azienda}` : ""} — l'operazione non può essere annullata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`admin-contact-delete-cancel-${c.id}`}>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={remove} disabled={busy} data-testid={`admin-contact-delete-confirm-${c.id}`} className="bg-[#E30613] hover:bg-[#C20410]">
                            {busy ? "Eliminazione…" : "Elimina"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export const MarkReadButton = ({ c, onChanged }) => {
    const [busy, setBusy] = useState(false);
    if ((c.status || "nuovo") !== "nuovo") return null;
    const mark = async () => {
        setBusy(true);
        try {
            await updateContact(c.id, "letto", c.note || "");
            toast.success("Segnata come letta");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };
    return (
        <button type="button" onClick={mark} disabled={busy} data-testid={`admin-contact-mark-read-${c.id}`} className={`${pill} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50`}>
            <Check className="h-3.5 w-3.5" /> Segna come letta
        </button>
    );
};
